import React, { useState, useEffect, useContext } from 'react';
import { Button, Card, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import MaterialTable, { MTableToolbar } from 'material-table';
import icons from 'MaterialTableIcons';
import { DetailsColumn } from 'Secure/Utils';
import { EngineIcon } from 'Utils';
import { getProviders, cancelMaskingJobById } from 'Secure/api';
import ProviderSelectCell from 'Secure/ProviderSelectCell';
import MaskButton from './MaskButton';
import AppContext from 'Utils/AppContext';
import { READ_ONLY_TITLE } from 'Auth/permissions';
import { getDisplayDate, getDisplayTime, getErrorMessage } from 'Utils';
import { FaShieldAlt } from 'react-icons/fa';
import { BsFillCircleFill } from 'react-icons/bs';
import { BiErrorCircle } from 'react-icons/bi';
import { STATUS, OPERATION } from 'Utils/styleTokens';
import DiscoveryTooltip from 'DiscoveryTooltip';

// status.includes('TERMINATING') || status.includes('STARTING') || status.includes('BOOTSTRAPPING')

const statusLabels = {
  MASKED: 'Secured',
  MASKING: 'Processing',
  RUNNING: 'Masking',
  UNSECURED: 'Unsecured',
  TERMINATING: 'Unsecured',
  BOOTSTRAPPING: 'Unsecured',
  COMPLETED: 'Secured',
  UNKNOWN: 'Processing',
  UNMASKED: 'Unmasked',
  UNMASKING: 'Unmasking',
  ERROR: 'ERROR',
};

// Semantic status dots (style-guide STATUS triads): success green, danger red, neutral grey for
// the "not yet secured" states, info blue while running/processing.
const statusColors = {
  MASKED: STATUS.success.dot,
  COMPLETED: STATUS.success.dot,
  DECRYPTED: STATUS.neutral.dot,
  UNSECURED: STATUS.neutral.dot,
  UNKNOWN: STATUS.info.dot,
  TERMINATING: STATUS.neutral.dot,
  MASKING: STATUS.info.dot,
  RUNNING: STATUS.info.dot,
  BOOTSTRAPPING: STATUS.neutral.dot,
  UNMASKED: STATUS.neutral.dot,
  ERROR: STATUS.danger.dot,
};

const ERROR_ICON_COLOR = STATUS.danger.dot;

function formatDuration(ms) {
  if (!ms || ms < 0) return '00:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/**
 * Tooltip for a running masking job — mirrors the discover progress bar: count of
 * tables/files done and how long the job has been running. The worker discovers work
 * items as it goes, so totals ("N of M") are deliberately never shown — the denominator
 * would keep growing.
 */
function maskingRunningTooltip(progress, unitNoun) {
  const lines = [];
  const done = progress && progress.tablesCompleted;
  if (done != null && !Number.isNaN(Number(done))) {
    const n = Number(done);
    lines.push(`${n.toLocaleString()} ${unitNoun}${n === 1 ? '' : 's'} processed`);
  }
  const startedAtMs = progress && progress.startedAtMs;
  if (startedAtMs != null) {
    lines.push(`Running for ${formatDuration(Date.now() - startedAtMs)}`);
  }
  if (lines.length === 0) return 'Masking in progress…';
  return (
    <div>
      {lines.map((line, i) => (
        <div key={i}>{line}</div>
      ))}
    </div>
  );
}

function SourcesTable({ maskOptions, data, fetchingData, fetchDataHandler }) {
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState(null);
  const [searchTerms, setSearchTerms] = useState('');
  const [searched, setSearched] = useState(false);
  const [displayedSearch, setDisplayedSearch] = useState(null);
  const [localStatusUpdates, setLocalStatusUpdates] = useState({});
  const [maskingProviders, setMaskingProviders] = useState([]);
  const [cancellingId, setCancellingId] = useState(null);
  const { setNotification, isReadOnly } = useContext(AppContext);
  const { sources, totalConnections } = data;
  const PAGE_SIZE = 10;

  // Providers for the PROVIDER column dropdown — fetched once for the whole table.
  useEffect(() => {
    let cancelled = false;
    getProviders('MASKING').then((list) => { if (!cancelled) setMaskingProviders(list); });
    return () => { cancelled = true; };
  }, []);

  // Handle status changes from MaskButton
  const handleStatusChange = (connectionName, newStatus) => {
    setLocalStatusUpdates(prev => ({
      ...prev,
      [connectionName]: newStatus
    }));
  };

  // Get the effective status for a source (local update or original)
  const getEffectiveStatus = (source) => {
    return localStatusUpdates[source.connection_name] || source.status;
  };

  // Cancel a running masking job from the row. The Mask button is a disabled "Masking.."
  // while a job runs, so this is the only in-table way to stop it; the job is cancellable
  // server-side via POST /masking/jobs/{id}/cancel.
  const handleCancel = async (source) => {
    const jobId = source.latestJobId;
    if (jobId == null) return;
    setCancellingId(jobId);
    try {
      await cancelMaskingJobById(jobId);
      setNotification({
        header: 'Masking job cancelling',
        body: <span>Requested cancellation of the masking job for {source.connection_name}.</span>,
      });
      if (fetchDataHandler) await fetchDataHandler();
    } catch (e) {
      setNotification({
        header: 'Cancel failed',
        body: <span>{getErrorMessage(e)}</span>,
      });
    } finally {
      setCancellingId(null);
    }
  };

  // Clear local status updates when new data is fetched from server
  useEffect(() => {
    if (sources && sources.length > 0) {
      // Clear all local status updates when we get fresh server data
      setLocalStatusUpdates({});
    }
  }, [sources]);

  const COLUMNS = [
    {
      title: 'DATA SOURCE',
      field: 'connection_name',
      // customSort: () => 0,
      // No explicit width: with tableLayout 'fixed', this column absorbs the leftover
      // horizontal space the other (fixed-width) columns don't use.
      cellStyle: { paddingRight: 0 },
      // Name links to the LAST masking job's results when one exists; before any job has run it
      // falls back to the data source editor (the results route needs a jobId).
      render: ({ connection_name, dataSourceId, engine, latestJobId }) => (
        <div className='d-flex align-items-center'>
          {engine ? <span className='mr-2'><EngineIcon engine={engine} /></span> : null}
          {latestJobId != null ? (
            <DiscoveryTooltip text='View results of the last masking job' placement='top'>
              <Link to={`/secure/mask/results/${encodeURIComponent(latestJobId)}`}>{connection_name}</Link>
            </DiscoveryTooltip>
          ) : dataSourceId != null ? (
            <Link to={`/sources/source-editor/${encodeURIComponent(dataSourceId)}`}>{connection_name}</Link>
          ) : (
            connection_name
          )}
        </div>
      )
    },
    // {
    //   title: 'DISCOVERY DATE',
    //   field: 'time_stamp',
    //   defaultSort: 'desc',
    //   // customSort: () => 0,
    //   width: 200,
    //   render: ({ time_stamp }) => (
    //     <div>{getDisplayDate(time_stamp)} @ {getDisplayTime(time_stamp)}</div>
    //   )
    // },
    {
      title: 'DETAILS',
      field: 'fieldQty',
      width: 250,
      render: ({ dataSourceId, fieldQty, prevSearchid }) => (
        <DetailsColumn
          dataSourceId={dataSourceId}
          fieldQty={fieldQty}
          mode='mask'
          prevSearchid={prevSearchid}
        />
      )
    },
    {
      title: 'PROVIDER',
      field: 'preferredMaskingProviderId',
      sorting: false,
      render: (row) => (
        <ProviderSelectCell row={row} providers={maskingProviders}
          field='preferredMaskingProviderId' defaultLabel='C2 Masking' />
      ),
      width: 180,
    },
    {
      title: 'STATUS',
      field: 'status',
      width: 150,
      headerStyle: { textAlign: 'center' },
      // Default row order is by most-recent job date (see getMaskingSources); this column keeps
      // material-table's own sort when the user clicks the header.
      render: (source) => {
        const effectiveStatus = getEffectiveStatus(source);
        const errorTooltipText =
          source.errorMessage && String(source.errorMessage).trim() !== ''
            ? String(source.errorMessage).trim()
            : 'Masking job failed; no error details were returned.';

        if (source.readOnly) {
          return (
            <div>
              <div className='d-flex justify-content-start'>
                <div className='d-flex flex-column justify-content-center pl-2 pr-2'>
                  <div>
                    <BsFillCircleFill color={STATUS.neutral.dot} />
                  </div>
                </div>
                <div>READ ONLY</div>
              </div>
            </div>
          );
        }

        if (effectiveStatus === 'ERROR') {
          return (
            <DiscoveryTooltip text={errorTooltipText} placement='top'>
              <div className='d-flex justify-content-start align-items-center pointer'>
                <div className='d-flex flex-column justify-content-center pl-2 pr-2'>
                  <BiErrorCircle style={{ color: ERROR_ICON_COLOR, fontSize: '1.25rem' }} aria-hidden />
                </div>
                <div className='text-danger'>{statusLabels.ERROR}</div>
              </div>
            </DiscoveryTooltip>
          );
        }

        if (effectiveStatus === 'RUNNING' || effectiveStatus === 'MASKING') {
          const progress = source.maskingJobProgress;
          const isFileSource = String(source.datasourceType || '').toUpperCase().startsWith('FILE_');
          const tooltipText = maskingRunningTooltip(progress, isFileSource ? 'file' : 'table');
          const recordsTotal = progress && progress.recordsTotal;
          const recordsCompleted = progress && progress.recordsCompleted;
          // Records percent, capped at 99 while running — same as the discover bar.
          let percent = null;
          if (recordsTotal != null && recordsTotal > 0 && recordsCompleted != null) {
            percent = Math.min(Math.floor((recordsCompleted / recordsTotal) * 100), 99);
          }
          return (
            <DiscoveryTooltip text={tooltipText} placement='top'>
              <div className='pr-1' style={{ minWidth: 108 }}>
                <div className='small text-muted mb-1'>{statusLabels.RUNNING}</div>
                {/* Mask concept is always violet (OPERATION.mask) — nested bar carries the fill color. */}
                {percent != null && percent >= 1 ? (
                  <ProgressBar style={{ height: 22 }}>
                    <ProgressBar
                      now={percent}
                      label={`${percent}%`}
                      style={{ backgroundColor: OPERATION.mask.color }}
                    />
                  </ProgressBar>
                ) : (
                  <ProgressBar style={{ height: 22 }}>
                    <ProgressBar
                      animated
                      striped
                      now={100}
                      style={{ backgroundColor: OPERATION.mask.color }}
                    />
                  </ProgressBar>
                )}
              </div>
            </DiscoveryTooltip>
          );
        }

        return (
          <div>
              <div className='d-flex justify-content-start'>
                { statusColors[effectiveStatus] && (
                  <div className='d-flex flex-column justify-content-center pl-2 pr-2'>
                    <div>
                      <BsFillCircleFill color={statusColors[effectiveStatus]} />
                    </div>
                  </div>
                )}
                <div>
                  { statusLabels[effectiveStatus] ? statusLabels[effectiveStatus] : effectiveStatus }
                </div>
              </div>
          </div>
        );
      }
    },
    {
      title: ' ',
      headerStyle: { textAlign: 'center' },
      sorting: false,
      render: (source) => {
        const { connection_name, dataSourceId, errorMessage, fieldQty, prevSearchid, hasMaskingDriver, readOnly, datasourceType, engine, latestJobId } = source;
        const effectiveStatus = getEffectiveStatus(source);
        if (effectiveStatus === 'RUNNING' || effectiveStatus === 'MASKING') {
          return (
            <div className='d-flex justify-content-center'>
              <DiscoveryTooltip text={isReadOnly ? READ_ONLY_TITLE : 'Cancel this masking job'} placement='top'>
                <div className='w-100'>
                  <Button
                    size='sm'
                    variant='outline-danger'
                    className='w-100'
                    data-testid='mask-cancel'
                    disabled={latestJobId == null || cancellingId === latestJobId || !!isReadOnly}
                    onClick={() => handleCancel(source)}
                  >
                    {cancellingId === latestJobId ? 'Cancelling…' : 'Cancel'}
                  </Button>
                </div>
              </DiscoveryTooltip>
            </div>
          );
        }
        return ( (effectiveStatus === 'MASAKED'  || effectiveStatus === 'MASKAING') ?
           (<div>
          </div>) :
           (
          <MaskButton
            connection={connection_name}
            datasourceType={datasourceType}
            engine={engine}
            dataSourceId={dataSourceId}
            status={effectiveStatus}
            errorMessage={errorMessage}
            fieldQty={fieldQty}
            prevSearchid={prevSearchid}
            hasMaskingDriver={hasMaskingDriver}
            readOnly={readOnly}
            onStatusChange={handleStatusChange}
          />
        ))
      },
      width: 250
    }
  ];

  const handleSearch = async () => {
    let searchTermsValue = searchTerms;
    let searchedValue = true;
    let displayedSearchValue = searchTerms;

    if (searched) {
      setSearchTerms('');
      searchTermsValue = null;
      searchedValue = false;
      displayedSearchValue = null;
    }

    await fetchDataHandler(0, null, searchTermsValue);
    setPage(0);
    setSearched(searchedValue);
    setDisplayedSearch(displayedSearchValue);
  }

  return (
    <Card>
      {/* <div className="widget-header d-flex p-3">
        <div className='w-50 text-left d-flex align-items-center'>
          <FaShieldAlt className="mr-2" />
          { displayedSearch ? `Displaying results for "${displayedSearch}"` : 'Encrypt / Decrypt Status'}
        </div>
        <div className='w-50 flex-grow-1 d-flex'>
          <Form.Control
            type='text'
            value={searchTerms}
            placeholder='Search discovered data sources'
            onChange={(e) => {
              setSearchTerms(e.target.value);
              setSearched(false);
            }}
          />
          <Button 
            variant={'outline-secondary'}
            disabled={fetchingData}
            className='pl-4 pr-4 h-100 ml-2'
            onClick={async () => await handleSearch()}
          >
            {fetchingData ? (
              <Spinner size='sm' animation='border' />
            ) : (
              <span>{searched ? 'X' : <FaSearch />}</span>
            )}
          </Button>
        </div>
      </div> */}
      <MaterialTable
        components={{
          Toolbar: props => (
            <div className="entity-panel-table-header d-flex">
              <div className="text-left widget-header p-3">
                <FaShieldAlt className="mr-2" />
                <span>Manage Masking</span>
              </div>
              <div className='d-flex flex-grow-1 justify-content-end'>
                <MTableToolbar { ...props } />
              </div>
            </div>
          )
        }}
        columns={COLUMNS}
        icons={icons}
        data={sources}
        isLoading={fetchingData}
        options={{
          showTitle: false,
          pageSize: PAGE_SIZE,
          // search: true,
          tableLayout: 'fixed',
          draggable: false
        }}
        // onOrderChange={async (colIndex, dir) => {
        //   if (COLUMNS[colIndex]) {
        //     const col = COLUMNS[colIndex];
        //     const sortField = col.field === 'connection_name' ? '_key' : col.field;
        //     const sortKeyValue = { [sortField]: dir };
        //     await fetchDataHandler(0, sortKeyValue);
        //     setSortKey(sortKeyValue);
        //     setPage(0);
        //   }
        // }}
        // components={{
        //   Pagination: props => (
        //     <MTablePagination
        //       {...props}
        //       classes={{root: props.classes.root}}
        //       count={totalConnections}
        //       showFirstLastPageButtons={false}
        //       page={page}
        //       onChangePage={async (e, pageValue) => {
        //         const fromValue = pageValue * PAGE_SIZE;
        //         await fetchDataHandler(fromValue, sortKey, (searched ? searchTerms : null));
        //         setPage(pageValue);
        //       }}
        //     />
        //   )
        // }}
      />
    </Card>
  )
}

export default SourcesTable;